import { Flex } from '@brightdrop/bd-ui';
import type { BoxProps } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  Typography,
  useTheme,
} from '@mui/material';
import type {
  MRT_Cell,
  MRT_Row,
  MRT_RowData,
  MRT_TableInstance,
} from 'material-react-table';
import type React from 'react';
import { Fragment, useCallback, useState } from 'react';
import ChevronDownIcon from '../assets/icons/chevron-down.svg?react';
import DotsHorizontalIcon from '../assets/icons/dots-horizontal.svg?react';
import type {
  AdaptedColumnDef,
  RenderCardedRowProps,
} from '../models/table.model';
import useStyles from './RenderCardedRow.styles';

/**
 * Resolves interactive props from a column's muiTableBodyCellProps for mobile card cells.
 * Desktop table cells receive these via MRT; card rows must apply them explicitly.
 */
const getCardCellInteractionProps = <TData extends MRT_RowData>(
  cell: MRT_Cell<TData, unknown>,
  row: MRT_Row<TData>,
  table: MRT_TableInstance<TData>
): Pick<BoxProps, 'onClick' | 'onKeyDown' | 'role' | 'sx' | 'tabIndex'> => {
  const { muiTableBodyCellProps } = cell.column.columnDef;
  if (!muiTableBodyCellProps) {
    return {};
  }

  const resolvedProps =
    typeof muiTableBodyCellProps === 'function'
      ? muiTableBodyCellProps({ cell, column: cell.column, row, table })
      : muiTableBodyCellProps;

  if (!resolvedProps?.onClick) {
    return {};
  }

  const { onClick, onKeyDown, role, sx, tabIndex } = resolvedProps;

  return {
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onClick(event as React.MouseEvent<HTMLTableCellElement>);
    },
    onKeyDown: onKeyDown
      ? (event: React.KeyboardEvent<HTMLElement>) => {
          event.stopPropagation();
          onKeyDown(event as React.KeyboardEvent<HTMLTableCellElement>);
        }
      : undefined,
    role,
    sx,
    tabIndex,
  };
};

/**
 * A reusable component for rendering a styled list item.
 *
 * @param {React.ReactNode} children - The content to render inside the list item.
 * @param {string | number} [key] - Optional key for the list item.
 * @param {object} [sx] - Additional styles for the list item.
 *
 * @returns {JSX.Element} The rendered list item.
 */
const BoxLi: React.FC<BoxProps> = ({ children, key, sx = {} }) => (
  <Box
    component="li"
    key={key}
    sx={{
      listStyleType: 'none',
      marginBottom: '4px',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * Renders a card-style layout for a single row, with options to handle nested objects and arrays.
 * The first column or any column marked as `isFixed` is treated as a fixed column and rendered with special styling.
 * Other columns are displayed as key-value pairs.
 *
 * @template TData - The type of data represented by each row.
 *
 * @param {RenderCardedRowProps<TData>} props - The component props.
 * @param {MRT_Row<TData>} props.row - The current row data.
 * @param {AdaptedColumnDef<TData>[]} props.columns - The column definitions for rendering the row.
 * @param {string} [props.defaultEmptyValue='N/A'] - Default value for empty or undefined cells.
 * @param {Record<string, string>} [props.valueLabels] - Optional label translations or mappings for values.
 * @param {(event: React.MouseEvent<HTMLDivElement>, row: MRT_Row<TData>) => void} [props.onClick] - Click handler for the row.
 *
 * @returns {JSX.Element} The rendered card layout.
 *
 * @example
 * const columns = [
 *   { key: 'checkbox', header: '', isFixed: true }, // Always rendered, even if hideColumn is true
 *   { key: 'name', header: 'Vehicle Name' },
 *   { key: 'status', header: 'Status' },
 *   { key: 'details', header: 'Details', hideColumn: true }, // Skipped unless marked as `isFixed`
 * ];
 *
 * <RenderCardedRow
 *   row={row}
 *   columns={columns}
 *   defaultEmptyValue="No Data"
 * />
 */
function RenderCardedRow<TData extends MRT_RowData>({
  row,
  columns,
  defaultEmptyValue = '--',
  table,
  valueLabels,
  onClick,
  renderAccordionMobileView,
  mobileTargetColumnIndex = 0,
  hideCardedRowCheckbox,
  showRowActionMenuItems,
  renderRowActionMenuItems,
}: RenderCardedRowProps<TData>): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [actionMenuAnchorEl, setActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const isActionMenuOpen = Boolean(actionMenuAnchorEl);

  interface NestedDataObject {
    [key: string]: NestedData;
  }

  type NestedData =
    | string
    | number
    | boolean
    | null
    | undefined
    | NestedData[]
    | NestedDataObject;

  /**
   * Recursively renders objects and arrays in a readable format.
   *
   * @param {NestedData} obj - The object, array, or primitive value to render.
   * @returns {React.ReactNode} The rendered representation of the object, array, or value.
   */
  const renderObject = useCallback((obj: NestedData): React.ReactNode => {
    if (Array.isArray(obj)) {
      return (
        <Box component="ul" sx={{ paddingLeft: 2, margin: 0 }}>
          {obj.map((item, index) => {
            if (item && typeof item === 'object' && 'row' in item) {
              // biome-ignore lint/suspicious/noArrayIndexKey: nested row data carries no identifier and may contain identical entries; the read-only list items hold no state
              return <BoxLi key={index}>{renderObject(item)}</BoxLi>;
            }
            return null;
          })}
        </Box>
      );
    }

    if (obj && typeof obj === 'object') {
      return (
        <Box component="ul" sx={{ paddingLeft: 2, margin: 0 }}>
          {Object.entries(obj).map(([key, value]) => (
            <BoxLi key={key}>
              <Typography
                component="span"
                variant="body_medium"
                sx={{ fontWeight: 'bold' }}
              >
                {key}:
              </Typography>{' '}
              <Typography component="span" variant="body_regular">
                {renderObject(value)}
              </Typography>
            </BoxLi>
          ))}
        </Box>
      );
    }

    return obj?.toString?.() || 'N/A';
  }, []);

  /**
   * Formats cell values, including handling nested objects and arrays.
   *
   * @param {unknown} value - The value to format.
   * @returns {React.ReactNode} The formatted value.
   */
  const formatValue = useCallback(
    (value: unknown): React.ReactNode =>
      renderObject(value as NestedDataObject),
    [renderObject]
  );

  // Extract the target column (or first column if target doesn't exist)
  const targetColumn = columns[mobileTargetColumnIndex] || columns[0];
  const otherColumns = columns.filter(
    (_, index) => index !== (mobileTargetColumnIndex || 0)
  );
  // Find the cell for the target column
  const targetCell = row
    .getAllCells()
    .find(
      (c) =>
        c.column.id === targetColumn.accessorKey ||
        c.column.columnDef.header === targetColumn.header
    );

  // Function to get the rendered content of a cell
  const getRenderedContent = (
    cell: MRT_Cell<TData, unknown> | undefined,
    column: AdaptedColumnDef<TData>
  ): React.ReactNode => {
    if (!cell) return defaultEmptyValue;

    if (column.Cell) {
      return column.Cell({
        cell,
        column: cell.column,
        renderedCellValue: cell.getValue<React.ReactNode>(),
        row,
        table,
        valueLabels,
        useCardedLayout: true,
      });
    }
    return formatValue(cell.getValue());
  };

  const handleActionMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
  };

  const rowActionMenuItems = renderRowActionMenuItems
    ? renderRowActionMenuItems({
        closeMenu: handleActionMenuClose,
        row,
        table,
      })
    : [];

  const firstCellSummary = () => (
    <Flex
      center
      style={{
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Flex>
        {!hideCardedRowCheckbox && row.getCanSelect() && (
          <Checkbox
            checked={row.getIsSelected()}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              row.toggleSelected();
            }}
            inputProps={{ 'aria-label': `Select row ${row.id}` }}
            data-testid={`checkbox-${row.id}`}
          />
        )}
        {targetCell && (
          <Box
            component="span"
            {...getCardCellInteractionProps(targetCell, row, table)}
          >
            <Typography component="span" variant="body_regular">
              {getRenderedContent(targetCell, targetColumn)}
            </Typography>
          </Box>
        )}
      </Flex>
      <Flex
        // Button needs to render in dom to anchor the menu
        style={{
          display:
            showRowActionMenuItems &&
            renderRowActionMenuItems &&
            rowActionMenuItems &&
            rowActionMenuItems.length > 0
              ? 'flex'
              : 'none',
        }}
        className={classes.rowActionButtonContainer}
      >
        <IconButton
          onClick={handleActionMenuClick}
          aria-label="row actions"
          aria-controls="row-menu"
          aria-haspopup="true"
          data-testid="row-action-menu-button"
        >
          <DotsHorizontalIcon />
        </IconButton>
        <Menu
          id="row-menu"
          anchorEl={actionMenuAnchorEl}
          open={isActionMenuOpen}
          onClose={handleActionMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {rowActionMenuItems}
        </Menu>
      </Flex>
    </Flex>
  );

  const getCell = (column: AdaptedColumnDef<TData, unknown>) => {
    if (column.hideColumn && !column.isFixed) return null;

    const cell = row
      .getAllCells()
      .find(
        (c) =>
          c.column.id === column.accessorKey ||
          c.column.columnDef.header === column.header
      );
    if (!cell) {
      return null;
    }

    return cell;
  };

  return (
    <Card
      sx={{
        marginBottom: 2,
        padding: 0,
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: row.getIsSelected()
          ? 'action.selected'
          : 'background.paper',
        '&:hover': {
          backgroundColor: onClick
            ? theme.new.color.brand[100]
            : 'background.paper',
        },
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event, row);
      }}
      data-testid={`carded-row-content-${row.id}`}
    >
      {renderAccordionMobileView ? (
        <Accordion
          disableGutters
          defaultExpanded
          className={classes.accordionContainer}
        >
          <AccordionSummary
            expandIcon={<ChevronDownIcon />}
            className={classes.accordionSummary}
            onClick={(e) => {
              // Only stop propagation if clicking on the expand icon area
              // Material-UI wraps the expandIcon in an element with class MuiAccordionSummary-expandIconWrapper
              const target = e.target as HTMLElement;
              const expandIconWrapper = target.closest(
                '.MuiAccordionSummary-expandIconWrapper'
              );
              if (expandIconWrapper) {
                e.stopPropagation();
              }
            }}
          >
            {firstCellSummary()}
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0px' }}>
            {otherColumns.map((column) => {
              const cell = getCell(column);

              if (!cell) return null;

              return (
                <Fragment key={column.id}>
                  <Box className={classes.cardItemContainer}>
                    {!column.hideHeader && (
                      <Box className={classes.cardItemLabel}>
                        {column.header || cell.column.columnDef.header}
                      </Box>
                    )}
                    <Box
                      className={classes.cardItemValue}
                      minWidth="fit-content"
                      textAlign="right"
                      {...getCardCellInteractionProps(cell, row, table)}
                    >
                      {getRenderedContent(cell, column)}
                    </Box>
                  </Box>
                </Fragment>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ) : (
        <>
          <CardContent className={classes.assetListCardContent}>
            <Box display="flex" marginBottom={2}>
              {firstCellSummary()}
            </Box>
            {otherColumns.map((column, index) => {
              const cell = getCell(column);

              if (!cell) return null;

              return (
                <Fragment key={column.id}>
                  {index > 0 && <Divider sx={{ marginY: '8px' }} />}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom="8px"
                    width="100%"
                  >
                    {!column.hideHeader && (
                      <Box className={classes.assetCardItemLabel}>
                        {column.header || cell.column.columnDef.header}
                      </Box>
                    )}
                    <Box
                      className={classes.assetCardItemValue}
                      textAlign="right"
                      {...getCardCellInteractionProps(cell, row, table)}
                    >
                      {getRenderedContent(cell, column)}
                    </Box>
                  </Box>
                </Fragment>
              );
            })}
          </CardContent>
        </>
      )}
    </Card>
  );
}

export default RenderCardedRow;
